/**
 * The Dashboard's date range: a preset picked in the bar's select, read as days in the org's
 * calendar ending on the org's today. The API compares it with as many days again before it.
 */

export const PRESETS = [
  { key: 'last-7', label: 'Last 7 days', days: 7 },
  { key: 'last-30', label: 'Last 30 days', days: 30 },
  { key: 'last-90', label: 'Last 90 days', days: 90 },
  { key: 'last-365', label: 'Last 365 days', days: 365 },
] as const

export type Preset = (typeof PRESETS)[number]['key']

/** What the Dashboard opens on (docs/design/canvas/Dashboard). */
export const DEFAULT_PRESET: Preset = 'last-30'

/** A range of days, both included, as the API takes them. */
export type Period = { from: string; to: string; days: number }

const DAY_MS = 24 * 60 * 60 * 1000

/** Today's date in `timeZone` (`2026-10-13`): `en-CA` writes a date as ISO does. */
export function todayIn(timeZone: string, now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now)
}

/** `isoDate` moved by `days`, counted on the calendar (UTC), so no clock change moves it. */
export function addDays(isoDate: string, days: number): string {
  return new Date(Date.parse(`${isoDate}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10)
}

/** The preset's days, ending on the org's today. */
export function periodOf(preset: Preset, timeZone: string, now: Date = new Date()): Period {
  const days = PRESETS.find((option) => option.key === preset)?.days ?? 30
  const to = todayIn(timeZone, now)
  return { from: addDays(to, 1 - days), to, days }
}
